import { useBreakpointValue as useBreakpointValueChakra } from '@chakra-ui/react';
import { isNil } from 'lodash';

import authApi from './authApi';
import { SOCIAL_PLATFORMS } from './constants';
import { logger } from './core/logger/logger';
import { generatePath } from './core/router/router.helpers';
import { getCreatorInstagramData, getCreatorTiktokData } from './creatorUtils';
import { CreatorType, YoutubeChannelType } from './types';
import { formatDate } from './utils';

export const getUrlSearchParams = (): URLSearchParams =>
  new URLSearchParams(window.location.search);

export const urlSearchParamsIncludes = (arr: Array<string>): boolean =>
  arr.every((val: string) =>
    Array.from(getUrlSearchParams().keys()).includes(val),
  );

export const getInstagramProfileUrl = (handle: string): string =>
  `https://www.instagram.com/${handle}`;

export const getTiktokProfileUrl = (handle: string): string =>
  `https://www.tiktok.com/@${handle}`;

export const getYoutubeChannelUrl = (channelId: string): string =>
  `https://www.youtube.com/channel/${channelId}`;

export const getPlatformUrl = (
  platform: SOCIAL_PLATFORMS,
  creator: CreatorType,
  ytChannel: YoutubeChannelType,
) => {
  const { handle } = getCreatorInstagramData(creator);
  const { tiktokHandle } = getCreatorTiktokData(creator);

  const mapSocialPlatformNameToPlatformHandleProperty = {
    [SOCIAL_PLATFORMS.instagram]: getInstagramProfileUrl(handle),
    [SOCIAL_PLATFORMS.tiktok]: getTiktokProfileUrl(tiktokHandle),
    [SOCIAL_PLATFORMS.youtube]: getYoutubeChannelUrl(
      ytChannel.ytChannelId ?? '',
    ),
  };

  return mapSocialPlatformNameToPlatformHandleProperty[platform];
};

export const formatYoutubeVideoUrl = (videoId: string) =>
  `https://www.youtube.com/watch?v=${videoId}`;

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getEchoLink = (
  href: string,
  creatorId?: string,
): string | void => {
  const accessToken = authApi.jwtAccessToken?.accessToken;
  const refreshToken = authApi.jwtAccessToken?.refreshToken;

  if (accessToken && refreshToken) {
    return generatePath(href, {
      query: {
        accessToken,
        refreshToken,
        creatorId,
      },
    });
  }
};

export const getMaxValue = (value: number, maxValue: number = 99): string => {
  if (value > maxValue) {
    return `+${maxValue}`;
  }
  return value.toString();
};

export const isSSR = typeof window === 'undefined';

export const convertPxToRems = (pixels: number): string | undefined => {
  if (!isSSR) {
    if (!isNil(pixels)) {
      const baseFontSizePixelsString = window
        .getComputedStyle(document.body)
        .getPropertyValue('font-size');
      const baseFontSizeInPx = parseFloat(
        baseFontSizePixelsString.replace('px', ''),
      );

      const rems = pixels / baseFontSizeInPx;

      return `${rems}rem`;
    }
  }

  return;
};

export const useBreakpointValue = (values: any) =>
  useBreakpointValueChakra(values, { ssr: false });

export const getRandomIntFromInterval = (min: number, max: number) =>
  Math.floor(Math.random() * max) + min;

export const isValidUrl = (urlString: string) => {
  let urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%@_.~+]*)*' + // validate port and path
      '(\\?[;&a-z\\d%@_.~+=-]*)?' + // validate query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const closest = (goal: number, arr: number[]) => {
  if (arr.length) {
    return arr.reduce((prev, curr) => {
      return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
    });
  }
};

export const validateEmail = (email: string, caseInsensitive = false) => {
  const emailRegex = /^[a-z0-9._%+-]+(?<!\.)@[a-z0-9.-]+\.[a-z]{2,24}$/;
  const regex = caseInsensitive
    ? new RegExp(emailRegex.source, 'i')
    : emailRegex;
  return regex.test(email);
};

/**
 * Detects if the browser is Safari version 18 or below
 * @returns {boolean} true if Safari version 18 or below, false otherwise
 */
export const isSafariBrowser = (): boolean => {
  if (isSSR) return false;

  const userAgent = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  return isSafari;
};

/**
 * Helper function to validate and format date for DatePicker
 * @param dateString - The date string to format
 * @returns Formatted date string in YYYY-MM-DD format or null if invalid
 */
export const formatDateForPicker = (
  dateString: string | null | undefined,
): string | null => {
  if (!dateString || typeof dateString !== 'string') return null;

  try {
    // Use the existing formatDate utility to validate the date
    const formattedDate = formatDate(dateString);

    if (!formattedDate) {
      return null;
    }

    // Convert to YYYY-MM-DD format for DatePicker compatibility
    const date = new Date(dateString);
    const isoDate = date.toISOString().split('T')[0];

    return isoDate;
  } catch (error) {
    logger
      .withMetadata({
        error: error instanceof Error ? error.message : 'Unknown error',
        dateString,
        stack: error instanceof Error ? error.stack : undefined,
      })
      .error('Error formatting date for picker');
    return null;
  }
};
